import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import Marquee from "@/components/Marquee";
import HeroStage from "@/components/HeroStage";
import Seo, { organizationJsonLd } from "@/components/Seo";
import PartnerWall from "@/components/PartnerWall";
import SpotlightSection from "@/components/SpotlightSection";

import LatestNews from "@/components/LatestNews";
import Logo from "@/components/Logo";
import Img from "@/components/Img";

export default function Home() {
  const { t, L } = useLang();
  const site = useSite();
  const s = site?.settings || {};
  const sectors = site?.sectors || [];
  const ventures = site?.ventures || [];
  const offices = site?.offices || [];

  return (
    <div data-testid="home-page">
      <Seo page="home" jsonLd={organizationJsonLd(s, L)} />

      <HeroStage />

      {/* ---------- CHAIRMAN ---------- */}
      <section data-testid="chairman-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
        <Reveal className="md:col-span-5">
          <div className="relative border border-border p-10 md:p-12 overflow-hidden" style={{ borderRadius: "var(--radius)" }}>
            <div className="text-primary" aria-hidden="true"><Logo markSize={56} siteName="Al Qalaa" /></div>
            <p className="mt-8 font-head text-2xl md:text-[1.7rem] leading-snug italic">“{L(s.chairman_quote)}”</p>
            <div className="mt-8 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/50 text-primary font-head text-lg">
                {(s.chairman_name || "A Q").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
              <div>
                <p className="font-semibold" data-testid="chairman-name">{s.chairman_name}</p>
                <p className="text-sm text-primary">{L(s.chairman_title)}</p>
              </div>
            </div>
          </div>
        </Reveal>
        <div className="md:col-span-7">
          <Reveal><p className="eyebrow mb-4">{t("chairman_eyebrow")}</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">{L(s.site_name_alt)}</h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">{L(s.about_intro)}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <Link to="/about" data-testid="chairman-about-link" className="btn-outline mt-9">
              {t("nav_about")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- SECTOR BENTO ---------- */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 md:pb-32">
        <SectionHead eyebrow={t("sectors_eyebrow")} title={t("sectors_title")} sub={t("sectors_sub")} />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {sectors.map((sec, i) => (
            <Reveal key={sec.id} delay={0.06 * i} className={i % 4 === 0 || i % 4 === 3 ? "md:col-span-7" : "md:col-span-5"}>
              <Link
                to="/sectors"
                data-testid={`sector-card-${sec.id}`}
                className="card-lift group relative block h-[380px] md:h-[440px] overflow-hidden border border-border"
                style={{ borderRadius: "var(--radius)" }}
              >
                <Img
                  src={sec.image}
                  width={1280}
                  sizes="(max-width: 768px) 100vw, 58vw"
                  alt={L(sec.name)}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
                <div className="absolute inset-x-0 bottom-0 p-7 md:p-9">
                  <p className="text-primary text-xs tracking-[0.3em] uppercase mb-3">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-head text-2xl md:text-4xl text-white">{L(sec.name)}</h3>
                  <p className="mt-3 max-w-lg text-sm text-white/70 leading-relaxed">{L(sec.description)}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(sec.capabilities || []).slice(0, 3).map((cap, ci) => (
                      <span key={ci} className="rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] text-white/85 backdrop-blur-sm">
                        {L(cap)}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="absolute top-6 end-6 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white opacity-0 transition-all duration-500 group-hover:opacity-100">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- JOINT VENTURES 2026 ---------- */}
      <section className="border-y border-border bg-card/40 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow={t("ventures_eyebrow")} title={t("ventures_title")} sub={t("ventures_sub")} />
          <div className="grid gap-6 md:grid-cols-2">
            {ventures.map((v, i) => (
              <Reveal key={v.id} delay={0.1 * i}>
                <Link
                  to="/joint-ventures"
                  data-testid={`venture-card-${v.id}`}
                  className="card-lift group relative block overflow-hidden border border-border bg-background"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <div className="relative h-60 overflow-hidden">
                    <Img src={v.image} width={960} sizes="(max-width: 768px) 100vw, 50vw" alt={v.name} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-108" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
                    <span className="absolute top-5 start-5 rounded-full bg-primary px-4 py-1.5 text-[11px] font-bold tracking-[0.2em] uppercase text-primary-foreground">
                      {v.date}
                    </span>
                  </div>
                  <div className="p-7 md:p-9">
                    <p className="eyebrow mb-3">{L(v.badge)}</p>
                    <h3 className="font-head text-3xl md:text-4xl">{v.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {t("jv_with")} <span className="text-foreground font-medium">{v.partner}</span> — {v.partner_origin}
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-foreground/75">{L(v.focus)}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm text-primary">
                      {t("nav_ventures")} <ArrowRight className="h-4 w-4 rtl:rotate-180 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- LATEST NEWS ---------- */}
      <LatestNews />

      {/* ---------- EDITORIAL MARQUEE ---------- */}
      <div className="py-20">
        <Marquee />
      </div>

      {/* ---------- GLOBAL REACH ---------- */}
      <section data-testid="global-reach" className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 md:pb-32">
        <SectionHead eyebrow={t("reach_eyebrow")} title={t("reach_title")} sub={t("reach_sub")} />
        <div className="grid gap-5 md:grid-cols-2">
          {offices.map((o, i) => (
            <Reveal key={o.id} delay={0.05 * i}>
              <div
                data-testid={`office-card-${o.id}`}
                className={`h-full border p-7 md:p-8 flex flex-col justify-between gap-6 ${o.is_hq ? "md:col-span-2 border-primary/50 bg-card" : "border-border bg-card/40"} card-lift`}
                style={{ borderRadius: "var(--radius)" }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-head text-2xl md:text-3xl">{L(o.country)}</h3>
                    {L(o.city) ? <p className="mt-1 text-sm text-muted-foreground">{L(o.city)}</p> : null}
                  </div>
                  <span className={`shrink-0 rounded-full px-3.5 py-1.5 text-[10px] font-bold tracking-[0.18em] uppercase ${o.is_hq ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>
                    {L(o.kind) || (o.is_hq ? t("hq_badge") : t("rep_badge"))}
                  </span>
                </div>
                {L(o.address) ? <p className="text-sm text-muted-foreground leading-relaxed">{L(o.address)}</p> : null}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <Link to="/offices" data-testid="reach-offices-link" className="btn-outline">{t("nav_offices")}</Link>
        </Reveal>
      </section>

      <SpotlightSection placement="show_home" />

      <PartnerWall />

      {/* ---------- CTA ---------- */}
      <section data-testid="home-cta" className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-24 flex flex-col md:flex-row md:items-center justify-between gap-10">
          <Reveal>
            <h2 className="font-head text-4xl md:text-5xl leading-[1.08] max-w-2xl">{L(s.cta_title)}</h2>
            <p className="mt-4 max-w-xl opacity-80">{L(s.cta_body)}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="home-cta-contact-btn"
              className="btn-gold"
              style={{ background: "hsl(var(--background))", color: "hsl(var(--foreground))" }}
            >
              {t("cta_button")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
